import { PaginatedResponse } from '../types/PaginatedResponse';

type UnwrapPaginatedResult<T, Key extends string> = Omit<
  PaginatedResponse<T>,
  'data'
> &
  Record<Key, T | undefined>;

type WrappedPaginatedRefetchResult<TResult, T, Key extends string> = Omit<
  TResult,
  'data'
> &
  Omit<PaginatedResponse<T>, 'data'> &
  Record<Key, T | undefined>;

export function unwrapPaginatedResponseDataAs<T, Key extends string>(
  response: PaginatedResponse<T> | undefined,
  as: Key,
): UnwrapPaginatedResult<T, Key> | undefined;

export function unwrapPaginatedResponseDataAs<
  T,
  Key extends string,
  TRefetchResult extends { data: PaginatedResponse<T> | undefined },
>(
  response: PaginatedResponse<T> | undefined,
  refetch: (...args: any[]) => Promise<TRefetchResult>,
  as: Key,
): UnwrapPaginatedResult<T, Key> & {
  refetch: (
    ...args: any[]
  ) => Promise<WrappedPaginatedRefetchResult<TRefetchResult, T, Key>>;
};

export function unwrapPaginatedResponseDataAs<T, Key extends string>(
  response: PaginatedResponse<T> | undefined,
  refetchOrAs: ((...args: any[]) => Promise<any>) | Key,
  asKey?: Key,
): any {
  if (typeof refetchOrAs === 'string') {
    if (!response) return undefined;
    const { data, ...rest } = response;
    return { ...rest, [refetchOrAs]: data };
  }

  const refetch = refetchOrAs;
  const as = asKey!;

  const wrappedRefetch = async (...args: any[]) => {
    const result = await refetch(...args);
    const { data: paginatedResponse, ...resultRest } = result;
    const { data, ...paginationRest } = paginatedResponse ?? {};
    return { ...resultRest, ...paginationRest, [as]: data };
  };

  const { data, ...rest } = response ?? {};
  return {
    ...rest,
    [as]: data,
    refetch: wrappedRefetch,
  };
}
